import type { DivisionId } from '../types/division.js';
import type { SessionResultRow } from '../types/championship.js';

/** Titre décerné en fin de saison championnat. */
export type DriverTitleKind = 'world_rx_champion' | 'division_champion';

export function driverTitleKindForDivision(division: DivisionId): DriverTitleKind {
  return division === 1 ? 'world_rx_champion' : 'division_champion';
}

export function formatDriverTitleLabel(division: DivisionId): string {
  if (division === 1) return 'Champion du monde de rallycross';
  return `Champion de Division ${division}`;
}

/**
 * Agrège les titres homonymes pour l’UI : une pastille par kind/division,
 * avec préfixe Nx si plusieurs victoires, et le(s) numéro(s) de saison.
 */
export function formatDriverTitleChips(
  titles: readonly Pick<DriverTitleRecord, 'kind' | 'division' | 'label' | 'seasonId'>[]
): string[] {
  if (titles.length === 0) return [];

  type Group = {
    kind: DriverTitleKind;
    division: DivisionId;
    label: string;
    seasonIds: number[];
  };
  const groups = new Map<string, Group>();

  for (const title of titles) {
    const key = `${title.kind}:${title.division}`;
    const existing = groups.get(key);
    if (existing) {
      existing.seasonIds.push(title.seasonId);
    } else {
      groups.set(key, {
        kind: title.kind,
        division: title.division,
        label: title.label.trim() || formatDriverTitleLabel(title.division),
        seasonIds: [title.seasonId],
      });
    }
  }

  return [...groups.values()].map((group) => {
    const seasons = [...new Set(group.seasonIds)].sort((a, b) => a - b);
    const count = seasons.length;
    const prefix = count > 1 ? `${count}× ` : '';
    const seasonPart = count > 1 ? ` · Saisons ${seasons.join(', ')}` : ` · Saison ${seasons[0]}`;
    return `${prefix}${group.label}${seasonPart}`;
  });
}

/** Total duels pour barème monnaie (legacy ou réussis+contrés). */
export function sessionResultOvertakeDuelsForScoring(row: {
  overtakeDuels?: number;
  overtakesSucceeded?: number;
  overtakesCountered?: number;
}): number {
  const succeeded = row.overtakesSucceeded;
  const countered = row.overtakesCountered;
  if (succeeded != null || countered != null) {
    return Math.max(0, succeeded ?? 0) + Math.max(0, countered ?? 0);
  }
  return Math.max(0, row.overtakeDuels ?? 0);
}

export function normalizeSessionResultOvertakes(row: SessionResultRow): {
  overtakesSucceeded: number;
  overtakesCountered: number;
  overtakeDuels: number;
} {
  const succeeded = Math.max(0, row.overtakesSucceeded ?? 0);
  const countered = Math.max(0, row.overtakesCountered ?? 0);
  if (row.overtakesSucceeded != null || row.overtakesCountered != null) {
    return {
      overtakesSucceeded: succeeded,
      overtakesCountered: countered,
      overtakeDuels: succeeded + countered,
    };
  }
  const legacy = Math.max(0, row.overtakeDuels ?? 0);
  return {
    overtakesSucceeded: legacy,
    overtakesCountered: 0,
    overtakeDuels: legacy,
  };
}

export interface DriverSeasonMeetingBestTour {
  meetingNumber: number;
  meetingId: number;
  circuitName?: string;
  diceRolls: number;
  sessionType: string;
}

export interface DriverTitleRecord {
  id?: number;
  driverId: number;
  seasonId: number;
  division: DivisionId;
  kind: DriverTitleKind;
  label: string;
  awardedAt?: string;
}

export interface DriverSeasonProfileStats {
  championshipPoints: number;
  rank: number;
  overtakesSucceeded: number;
  overtakesCountered: number;
  bestMeetingTours: DriverSeasonMeetingBestTour[];
  finalsWon: number;
  podiums: number;
}

export interface DriverSeasonProfile {
  driverId: number;
  seasonId: number;
  titles: DriverTitleRecord[];
  season: DriverSeasonProfileStats;
}
